import React, { useState } from 'react';
import Menu from '@mui/material/Menu/Menu';
import MenuItem from '@mui/material/MenuItem/MenuItem';
import Text from './Text.js';
import Button from './Button.js';
import i18n from '../localization/i18n.js';
import tokens from '../localization/tokens.js';

/**
 * Component for rendering a menu of actions for a member.
 * 
 * Props:
 * - waiveCoverage: Function to handle waiving coverage action.
 * - reinstateCoverage: Function to handle reinstating coverage action.
 * - isMasterSite: Boolean indicating if it's being opened from the master site.
 * - sendPasswordReset: Function to handle sending password reset action.
 * - terminate: Function to handle termination action.
 * 
 * This component displays a button triggering a menu with member action options,
 * such as waiving coverage, reinstating coverage, sending password reset (if on a master site),
 * and terminating membership. Each action is represented as a MenuItem.
 */
const MemberActionsMenu = ({
  waiveCoverage,
  reinstateCoverage,
  showReinstateCoverageOption = true,
  isMasterSite = false,
  sendPasswordReset,
  terminate
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const buttonStyles = {
    height: 50,
    width: 220,
    marginLeft: "10px",
    textTransform: "capitalize",
    fontSize: 16,
    alignSelf: 'flex-end',
    marginBottom: "20px"
  };

  /**
   * Toggles the visibility of the menu.
   * 
   * @param {*} event The click event object.
   */
  const toggleMenu = event => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(!menuOpen);
  };

  /**
   * Closes the menu.
   */
  const closeMenu = () => {
    setMenuOpen(false);
    setAnchorEl(null);
  };

  /**
   * Renders a MenuItem with specified label and action.
   * 
   * @param {*} label The label text to display.
   * @param {*} action The function to execute onClick.
   * @returns The rendered MenuItem component.
   */
  const renderMenuItem = (label, action) => /*#__PURE__*/React.createElement(MenuItem, {
    onClick: () => {
      action();
      closeMenu();
    },
    sx: {
      width: 220
    }
  }, /*#__PURE__*/React.createElement(Text, {
    color: "inherit",
    medium: true,
    display: "inline"
  }, label));
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    color: "white",
    customStyle: buttonStyles,
    onClick: toggleMenu
  }, i18n.t(tokens.labels.employees.memberActions)), /*#__PURE__*/React.createElement(Menu, {
    anchorEl: anchorEl,
    open: Boolean(anchorEl),
    onClose: closeMenu,
    disableAutoFocusItem: true
  }, renderMenuItem(i18n.t(tokens.labels.changeRequests.waiveCoverage), waiveCoverage), showReinstateCoverageOption && renderMenuItem(i18n.t(tokens.labels.changeRequests.reinstateCoverage), reinstateCoverage), isMasterSite && renderMenuItem("Send Password Reset", sendPasswordReset), renderMenuItem(i18n.t(tokens.buttons.terminate), terminate)));
};
export default MemberActionsMenu;